import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AuthContext } from '@/features/auth/auth-context';
import { ApiError } from '@/services/api/errors';
import { ReferenceApi } from '@/services/api/reference-api';
import type { CountryOption } from '@/services/api/types';

export interface UseCountryOptionsResult {
  options: CountryOption[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetches the country list on mount for country pickers in the registration wizard.
 * Mirrors SpectraX's useCountryOptions but uses MDenture's auth context shape.
 */
export function useCountryOptions(reference?: ReferenceApi): UseCountryOptionsResult {
  const auth = useContext(AuthContext);

  const injectedApi = useRef(reference);
  useEffect(() => {
    injectedApi.current = reference;
  }, [reference]);
  const realApiRef = useRef<ReferenceApi | null>(null);

  const getApi = useCallback((): ReferenceApi => {
    if (injectedApi.current) {
      return injectedApi.current;
    }
    const client = auth?.client;
    if (!client) {
      throw new ApiError('unknown', 'generic');
    }
    if (!realApiRef.current) {
      realApiRef.current = new ReferenceApi(client);
    }
    return realApiRef.current;
  }, [auth]);

  const [options, setOptions] = useState<CountryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const countries = await getApi().getCountries(controller.signal);
        if (!mountedRef.current) {
          return;
        }
        setOptions(countries);
      } catch (err) {
        if (!mountedRef.current) {
          return;
        }
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        setError(err instanceof ApiError ? err.message : 'generic');
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    })();
    return () => {
      controller.abort();
    };
  }, [getApi]);

  return { options, loading, error };
}
